import { useRef } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';

import Button from '@klv/atoms/button';
import FeaturesItem from '@klv/molecules/features-item';
import type { IFeature } from '@klv/molecules/features-item/types';
import { getColorVariantFromBackground, hash } from '@klv/shared/utils/functions';
import useStorage from '@klv/hooks/useStorage';
import Grid from '@klv/utils/grid';
import type { IFeaturesListProps } from './types';
import * as s from './styles';

const FeaturesList = ({
  background, body, features, heading = '', loadMore, type,
}: IFeaturesListProps) => {
  const { t } = useTranslation();

  const featuresId = useRef(`features-${hash(JSON.stringify(features))}`);
  const [loadedMore, setLoadedMore] = useStorage(featuresId.current, false, 'sessionStorage');
  return (
    <s.StyledFeaturesList $variant={background}>
      <Grid>
        <s.FeaturesHeader>
          {heading && <s.StyledHeadline forwardedAs="h2" variant="h3" colorVariant={getColorVariantFromBackground(background)}>{heading}</s.StyledHeadline>}
          {body && <s.StyledBody
            colorVariant={getColorVariantFromBackground(background)}
            variant="paragraph2" 
            dangerouslySetInnerHTML={{ __html: body }}
          />}
        </s.FeaturesHeader>
      </Grid>
      {features.map((feature: IFeature, index: number) => {
        if (index < loadMore || (loadedMore)) {
          return (
            <FeaturesItem
              variant={background}
              feature={feature}
              type={type}
              key={JSON.stringify(feature)}
            />
          );
        }
        return null;
      })}
      {features.length > loadMore
      && !loadedMore
      && (
        <Grid>
          <s.ButtonContainer>
            <Button
              action={() => { setLoadedMore(true); }}
              variant="ghost"
              text={t('organisms_featuresList_loadMore')}
              buttonColor={background === 'light' ? 'dark' : 'light'}
            />
          </s.ButtonContainer>
        </Grid>
      )}
    </s.StyledFeaturesList>
  );
};

export default FeaturesList;
